import type {
  CollageAnswer,
  CollageContext,
  CollageFill,
  CollagePhoto,
  CollageRef,
  CollageSlotGeom,
  ImageCrop,
  Scene,
  SceneElement,
} from '@pz/scene';
import {
  COLLAGE_MAX_ZOOM,
  collageAutoCrop,
  collageSpares,
  exchangeCollage,
  fillRepeats,
  photoCssFilter,
  photoPlacement,
  randomCollageSeed,
  removeCollagePhoto,
  removeFromSlot,
  resetSlotCrop,
  sameCollageFrame,
  setSlotCrop,
  shapeDef,
  shuffleCollage,
} from '@pz/scene';
import { blackWhiteMask, turnStepOf } from '../canvas-edit.js';
import { openPhotoFramer, type FramerOptions } from '../photo-framer.js';
import { PHOTO_TEXTS } from '../photoquality.js';
import type { CollageBoardHost, CollageBoardOptions, CollageRender } from './board-types.js';
import { COLLAGE_TEXTS, collageSheetLabel } from './texts.js';
import {
  decideTap,
  GestureMachine,
  type BoardSelection,
  type GestureAction,
  type GesturePoint,
  type HitTarget,
  type PointerKind,
} from './gestures.js';
import {
  DOUBLE_TAP_ZOOM,
  OVERSCROLL,
  clampView,
  detailRenderScale,
  fitView,
  focusOn,
  panBy,
  refitView,
  sameView,
  sheetRenderScale,
  slotAt,
  viewScale,
  visibleSceneRect,
  zoomAt,
  zoomLimitsFor,
  type BoardView,
  type Rect,
  type Size,
} from './view.js';

/**
 * "Raspored slika" — the collage board (docs/photo-collage.md §1 D12, §7, §7.1).
 *
 * On a phone the wizard's preview is a third of the screen, and an A3 sheet
 * of 24 photos in it is 24 postage stamps. The board gives the print side the
 * whole screen (a large dialog on a desktop): pinch, pan and double-tap zoom,
 * tap a photo then another to swap them, tap the chosen one again to frame it
 * in the same full-screen framer single photos use, long-press (or drag with
 * a mouse) to move one. Every change of the answer is a pure `@pz/scene`
 * command handed to `host.commit`, so what the board does is what the server
 * validates and the print draws.
 *
 * Rendering. The sheet is drawn once at fit by the print renderer
 * (`host.render`, long side ≤ 2048 device px) and scaled with CSS while the
 * shopper zooms; when the view has been still for 160 ms at 1.25× or more a
 * second render of only the visible part is laid over it, sharp. A slot whose
 * photo just changed shows its thumbnail as a stand-in until the next render
 * lands. When there is no renderer (an old cached bundle, WASM out of memory,
 * nothing in 6 s) the whole board runs on stand-ins — slot outlines with each
 * slot's thumbnail placed exactly as the renderer places the photo — and every
 * action still works.
 *
 * Memory. Thumbnails (256 px) for stand-ins and the drag ghost, the 2400 px
 * copy only inside the framer, two canvases at screen size; everything is
 * released when the board closes.
 */

// ── Words ────────────────────────────────────────────────────────────────────

/**
 * The board's Serbian words (docs/photo-collage.md §7): the collage's one list
 * (texts.ts), shared with the step so a phrase changes in one place.
 */
export const BOARD_TEXTS = COLLAGE_TEXTS;

// ── Timing and sizes ─────────────────────────────────────────────────────────

/** The view must be still this long before the sharp part is rendered. */
export const DETAIL_DELAY_MS = 160;
/** Zoom from which the sharp part is worth rendering. */
export const DETAIL_MIN_ZOOM = 1.25;
/** Nothing from the renderer in this long: stand-ins. */
export const RENDER_TIMEOUT_MS = 6000;
/** After an edit, the sheet is rendered again once edits pause this long. */
export const RERENDER_DELAY_MS = 120;
/** A stand-in over a changed slot fades at the latest after this long. */
export const PENDING_MAX_MS = 4000;
/** How often the board looks for answer changes it did not make (uploads landing, the picker). */
export const WATCH_MS = 300;
/** How long a notice about picked photos (refused, not sent) stays on the board. */
export const NOTICE_MS = 8000;
/** The FLIP animation of a swap. */
export const FLIP_MS = 180;
/** Stage padding around the sheet at fit. */
const STAGE_PAD = 12;
/** Near a stage edge while dragging, the view pans (px from the edge, px per frame). */
const EDGE = 40;
const EDGE_SPEED = 14;
/** One tap of the zoom buttons. */
const ZOOM_STEP = 1.5;

// ── Dependencies (injected by tests) ─────────────────────────────────────────

export interface BoardDeps {
  openFramer(options: FramerOptions): Promise<ImageCrop | undefined>;
  now(): number;
  setTimeout(fn: () => void, ms: number): number;
  clearTimeout(id: number | undefined): void;
  setInterval(fn: () => void, ms: number): number;
  clearInterval(id: number | undefined): void;
  raf(fn: () => void): number;
  cancelRaf(id: number | undefined): void;
  vibrate(ms: number): void;
  dpr(): number;
  random(): number;
}

/**
 * What a host may offer beyond the fixed §7.1 interface (host.ts offers
 * both). The board works without either.
 */
export interface CollageBoardHostExtras {
  /** Any asset's URL (a template's mask), where the host has a general lookup. */
  assetUrl?(assetId: string): string | undefined;
  /**
   * Hears what became of photos picked from the board — refused for lack of
   * room, not sent, unusable — so the shopper reads it on the board and not
   * on the step hidden behind it. Returns the unsubscribe.
   */
  onNotice?(listener: (text: string) => void): () => void;
  /** A sheet's name for the shopper (the print side's own name), when the collage spans several sheets. */
  sheetName?(index: number): string;
}

function browserDeps(): BoardDeps {
  const w = window;
  return {
    openFramer: openPhotoFramer,
    now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
    setTimeout: (fn, ms) => w.setTimeout(fn, ms),
    clearTimeout: (id) => w.clearTimeout(id),
    setInterval: (fn, ms) => w.setInterval(fn, ms),
    clearInterval: (id) => w.clearInterval(id),
    raf: (fn) => (typeof w.requestAnimationFrame === 'function' ? w.requestAnimationFrame(fn) : w.setTimeout(fn, 16)),
    cancelRaf: (id) => {
      if (id === undefined) return;
      if (typeof w.cancelAnimationFrame === 'function') w.cancelAnimationFrame(id);
      else w.clearTimeout(id);
    },
    vibrate: (ms) => {
      try {
        navigator.vibrate?.(ms);
      } catch {
        /* not allowed here; the ghost still says it */
      }
    },
    dpr: () => w.devicePixelRatio || 1,
    random: Math.random,
  };
}

// ── Icons ────────────────────────────────────────────────────────────────────

const icon = (paths: string, size = 18): string =>
  `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
const ICONS = {
  undo: icon('<path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/>'),
  shuffle: icon('<path d="M16 3h5v5"/><path d="M4 20 21 3"/><path d="M21 16v5h-5"/><path d="M15 15l6 6"/><path d="M4 4l5 5"/>'),
  add: icon('<path d="M12 5v14M5 12h14"/>'),
  frame: icon('<path d="M6 2v14a2 2 0 0 0 2 2h14"/><path d="M18 22V8a2 2 0 0 0-2-2H2"/>'),
  auto: icon('<path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/>'),
  replace: icon('<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"/>'),
  remove: icon('<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/>'),
  repeat: icon('<rect x="8" y="8" width="13" height="13" rx="2"/><path d="M4 16V5a2 2 0 0 1 2-2h11"/>'),
  swap: icon('<path d="M7 4 3 8l4 4"/><path d="M3 8h14"/><path d="m17 20 4-4-4-4"/><path d="M21 16H7"/>'),
  cancel: icon('<path d="M18 6 6 18M6 6l12 12"/>'),
  prev: icon('<path d="m15 18-6-6 6-6"/>', 20),
  next: icon('<path d="m9 18 6-6-6-6"/>', 20),
  zoomIn: icon('<path d="M12 6v12M6 12h12"/>', 20),
  zoomOut: icon('<path d="M6 12h12"/>', 20),
  fit: icon('<path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>', 20),
  face: icon('<circle cx="12" cy="12" r="9"/><path d="M9 10h.01M15 10h.01"/><path d="M9 15c1.7 1.3 4.3 1.3 6 0"/>', 14),
};

// ── Geometry of the slots ────────────────────────────────────────────────────

interface SlotBox {
  id: string;
  /** 1-based position among the ACTIVE slots ("Mesto n od N"). */
  n: number;
  cx: number;
  cy: number;
  w: number;
  h: number;
  rotation: number;
  geom: CollageSlotGeom;
  element: Extract<SceneElement, { type: 'image' }> | undefined;
  /** The print sheet (index into the board's sheets) the slot is on. */
  sheet: number;
}

function slotBoxes(slots: readonly CollageSlotGeom[], scenes: readonly Scene[]): SlotBox[] {
  const byId = new Map<string, { el: SceneElement; sheet: number }>();
  scenes.forEach((scene, sheet) => {
    for (const el of scene.elements) if (!byId.has(el.id)) byId.set(el.id, { el, sheet });
  });
  return slots.map((geom, i) => {
    const hit = byId.get(geom.elementId);
    const el = hit?.el;
    const image = el && el.type === 'image' ? el : undefined;
    const t = el?.transform;
    return {
      id: geom.elementId,
      n: i + 1,
      cx: geom.cx ?? t?.cx ?? 0,
      cy: geom.cy ?? t?.cy ?? 0,
      w: geom.w,
      h: geom.h,
      rotation: t?.rotation ?? 0,
      geom,
      element: image,
      sheet: hit?.sheet ?? 0,
    };
  });
}


/** A slot the author locked against both moving and scaling its photo gets no framer. */
function frameable(box: SlotBox | undefined): boolean {
  const shopper = box?.element?.shopper;
  return !(shopper?.move === false && shopper?.scale === false);
}

/** A clip shape's unit-box outline as an SVG mask stretched over the box (canvas-edit's own drawing). */
const shapeMasks = new Map<string, string>();
function shapeMaskUrl(kind: string): string {
  let url = shapeMasks.get(kind);
  if (!url) {
    const def = shapeDef(kind);
    const rule = def.fillRule ? ` fill-rule="${def.fillRule}"` : '';
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="100" height="100" preserveAspectRatio="none"><path transform="scale(100)" d="${def.d}" fill="#000"${rule}/></svg>`;
    url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    shapeMasks.set(kind, url);
  }
  return url;
}

function setMask(element: HTMLElement, url: string | undefined): void {
  const value = url ? `url("${url.replace(/"/g, '%22')}")` : '';
  const style = element.style as CSSStyleDeclaration & { webkitMaskImage?: string };
  style.maskImage = value;
  style.webkitMaskImage = value;
}

/** Where a layer opened from the board lives: the widget's shadow root. */
function layerHostOf(node: HTMLElement): ShadowRoot | HTMLElement {
  const root = typeof node.getRootNode === 'function' ? (node.getRootNode() as Node & { host?: Element }) : undefined;
  if (root && (root as ShadowRoot).host) return root as ShadowRoot;
  return (typeof document !== 'undefined' && document.body) || node;
}

function fillKey(fill: CollageFill | undefined): string {
  if (!fill) return '';
  const c = fill.crop;
  return `${fill.assetId}|${c ? [c.cx, c.cy, c.zoom, c.rotation ?? 0].join(',') : ''}|${fill.manual ? 'm' : ''}`;
}

function photoOf(answer: CollageAnswer | null, assetId: string): CollagePhoto | undefined {
  return answer?.photos.find((p) => p.assetId === assetId);
}

function refOf(selection: BoardSelection): CollageRef {
  return selection.kind === 'slot' ? { slot: selection.id } : { spare: selection.id };
}

function sameSelection(a: BoardSelection | null, b: BoardSelection | null): boolean {
  return a === b || (!!a && !!b && a.kind === b.kind && a.id === b.id);
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function actionButton(label: string, svg: string, action: string, className = 'pz-btn-ghost'): HTMLButtonElement {
  const button = el('button', className);
  button.type = 'button';
  button.innerHTML = `${svg}<span>${label}</span>`;
  button.title = label;
  button.setAttribute('data-action', action);
  return button;
}

function iconButton(label: string, svg: string, className: string, action: string): HTMLButtonElement {
  const button = el('button', className);
  button.type = 'button';
  button.innerHTML = svg;
  button.title = label;
  button.setAttribute('aria-label', label);
  button.setAttribute('data-action', action);
  return button;
}

/** Walks up from an event target to the nearest element carrying `attr`, stopping at `stop`. */
function closestWith(start: unknown, attr: string, stop: HTMLElement): HTMLElement | undefined {
  let node = start as (HTMLElement & { parentElement?: HTMLElement | null }) | null;
  while (node && node !== stop) {
    if (typeof node.getAttribute === 'function' && node.getAttribute(attr) !== null) return node;
    node = node.parentElement ?? null;
  }
  return undefined;
}

/** Paints a render into a canvas; false when the page cannot (no 2D context, no ImageData). */
function paint(canvas: HTMLCanvasElement, render: CollageRender): boolean {
  if (typeof ImageData === 'undefined') return false;
  canvas.width = render.width;
  canvas.height = render.height;
  const context = canvas.getContext('2d');
  if (!context) return false;
  // Copied out: the renderer's buffer may be backed by WASM memory, which
  // ImageData will not take directly.
  const bytes = new Uint8ClampedArray(render.pixels.byteLength);
  bytes.set(render.pixels);
  context.putImageData(new ImageData(bytes, render.width, render.height), 0, 0);
  return true;
}

// ── The board ────────────────────────────────────────────────────────────────

/**
 * Opens the board; resolves when it closes ("Gotovo", Escape, or the host
 * closing its layer). `deps` is for tests; the storefront passes nothing.
 */
export async function openCollageBoard(
  host: CollageBoardHost,
  opts: CollageBoardOptions = {},
  deps: BoardDeps = browserDeps(),
): Promise<void> {
  return new Promise<void>((resolve) => {
    const texts = BOARD_TEXTS;
    const mouse = host.pointer === 'mouse';
    const limits = zoomLimitsFor(host.pointer);
    const rules = host.rules;
    // The print sheets: one, or several when the collage spreads over more
    // than one print side (a box printed on two A3 sheets, each its own file).
    const sheetScenes = (): Scene[] => {
      const many = typeof host.printScenes === 'function' ? host.printScenes() : undefined;
      return many && many.length > 1 ? many : [host.printScene()];
    };
    const firstScenes = sheetScenes();
    const sheetCount = firstScenes.length;
    const multi = sheetCount > 1;
    const activeSlots = host.activeSlots();
    const ctx: CollageContext = { slots: activeSlots, rules };
    /** Every active slot, on every sheet ("Mesto n od N" counts them all). */
    const allBoxes = slotBoxes(activeSlots, firstScenes);
    const boxById = new Map(allBoxes.map((b) => [b.id, b] as const));
    const activeIds = allBoxes.map((b) => b.id);
    const total = allBoxes.length;
    /** The sheet on the stage: the one holding the focused slot, else the first. */
    let cur = opts.focus ? (boxById.get(opts.focus)?.sheet ?? 0) : 0;
    let sceneSize: Size = { w: firstScenes[cur]!.width, h: firstScenes[cur]!.height };
    /** The slots on the stage's sheet: what is drawn and hit. */
    let boxes = allBoxes.filter((b) => b.sheet === cur);

    let known: CollageAnswer | null = host.answer();
    let sceneFor: { answer: CollageAnswer | null; scenes: Scene[] } | undefined = { answer: known, scenes: firstScenes };
    let selection: BoardSelection | null = null;
    /** "Zameni mesto" was pressed: the next slot or spare tapped is exchanged with the selection. */
    let swapping = false;
    /** Slots whose hand framing came in from a slot of other proportions or outline: "Proveri kadar". */
    const recheck = new Set<string>();
    let view: BoardView = { fit: 1, zoom: 1, x: 0, y: 0 };
    let stageSize: Size = { w: 0, h: 0 };
    let stageRect = { left: 0, top: 0 };
    let closed = false;
    let framing = false;
    let lastPointerAt = -Infinity;
    let pressOrigin: 'stage' | 'strip' = 'stage';
    let standIn = false;
    let loading = true;
    let renderFailures = 0;
    let rendererMissing = false;
    /** A pan, pinch or drag is under way: no detail render, no view animation. */
    let gestureActive = false;
    /** The "Rezerva" buttons, by asset id. */
    const spareButtons = new Map<string, HTMLButtonElement>();

    const timers = new Set<number>();
    const later = (fn: () => void, ms: number): number => {
      const id = deps.setTimeout(() => {
        timers.delete(id);
        if (!closed) fn();
      }, ms);
      timers.add(id);
      return id;
    };
    const cancel = (id: number | undefined): void => {
      if (id === undefined) return;
      timers.delete(id);
      deps.clearTimeout(id);
    };
    const cleanups: Array<() => void> = [];
    const listen = (
      target: { addEventListener: EventTarget['addEventListener']; removeEventListener: EventTarget['removeEventListener'] },
      type: string,
      handler: (event: never) => void,
      options?: AddEventListenerOptions,
    ): void => {
      target.addEventListener(type, handler as unknown as EventListener, options);
      cleanups.push(() => target.removeEventListener(type, handler as unknown as EventListener, options));
    };

    // ── Sheet names ────────────────────────────────────────────────────────
    const extras = host as CollageBoardHost & CollageBoardHostExtras;
    function sheetFullName(i: number): string {
      const own = typeof extras.sheetName === 'function' ? extras.sheetName(i) : undefined;
      return (own ?? firstScenes[i]?.name ?? '').trim() || texts.sheet(i + 1);
    }
    /** The switcher's word for a sheet: its own name when short, else "List 2". */
    function sheetLabel(i: number): string {
      return collageSheetLabel(sheetFullName(i), i);
    }

    // ── DOM ────────────────────────────────────────────────────────────────
    const card = el('div', `pz-board-card is-loading ${mouse ? 'is-mouse' : 'is-touch'}`);
    card.setAttribute('aria-label', texts.title);

    const head = el('header', 'pz-board-head');
    const titleBox = el('div', 'pz-board-title');
    const title = el('strong', '', texts.title);
    const subtitle = el('span', '', host.label && host.label !== texts.title ? host.label : '');
    subtitle.hidden = !subtitle.textContent;
    titleBox.append(title, subtitle);
    const count = el('span', 'pz-board-count');
    const done = el('button', 'pz-btn pz-board-done', texts.done);
    done.type = 'button';
    head.append(titleBox, count, done);

    const main = el('div', 'pz-board-main');
    const viewBox = el('div', 'pz-board-view');
    const stage = el('div', 'pz-board-stage');
    stage.tabIndex = -1;
    stage.setAttribute('aria-label', mouse ? texts.hintMouse : texts.hintTouch);
    const sheetWorld = el('div', 'pz-board-world pz-board-sheetworld');
    const sheet = el('canvas', 'pz-board-sheet');
    const standinLayer = el('div', 'pz-board-standins');
    sheetWorld.append(sheet, standinLayer);
    const detail = el('canvas', 'pz-board-detail');
    detail.hidden = true;
    const hitWorld = el('div', 'pz-board-world pz-board-hits');
    const ghost = el('div', 'pz-board-ghost');
    ghost.hidden = true;
    stage.append(sheetWorld, detail, hitWorld, ghost);

    const zoomBar = el('div', 'pz-board-zoom');
    const zoomOut = iconButton(texts.zoomOut, ICONS.zoomOut, 'pz-board-zbtn', 'zoom-out');
    const zoomIn = iconButton(texts.zoomIn, ICONS.zoomIn, 'pz-board-zbtn', 'zoom-in');
    const zoomFit = iconButton(texts.fit, ICONS.fit, 'pz-board-zbtn', 'zoom-fit');
    zoomFit.innerHTML = `${ICONS.fit}<span>${texts.fit}</span>`;
    zoomBar.append(zoomOut, zoomIn, zoomFit);
    const loadingNote = el('div', 'pz-board-loading', texts.loading);
    viewBox.append(stage, zoomBar, loadingNote);

    const side = el('div', 'pz-board-side');
    const strip = el('div', 'pz-board-spares');
    strip.hidden = true;
    const stripTitle = el('span', 'pz-board-spares-title', texts.sparesTitle);
    strip.append(stripTitle);
    const bar = el('div', 'pz-board-bar');
    // What became of photos picked from here ("Dodaj slike", "Nova slika"):
    // refused for lack of room, not sent. The step behind the board says it
    // too, but nobody sees the step while the board is open.
    const notice = el('p', 'pz-board-notice');
    notice.hidden = true;
    side.append(strip, notice, bar);
    main.append(viewBox, side);

    const live = el('div', 'pz-board-live');
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    // Several sheets: a switcher between the header and the stage.
    const sheetBar = multi ? el('div', 'pz-board-sheets') : undefined;
    const sheetTabs: HTMLButtonElement[] = [];
    if (sheetBar) {
      sheetBar.setAttribute('role', 'group');
      sheetBar.setAttribute('aria-label', texts.sheets);
      for (let i = 0; i < sheetCount; i++) {
        const tab = el('button', 'pz-board-sheettab');
        tab.type = 'button';
        tab.setAttribute('data-sheet', String(i));
        tab.title = sheetFullName(i);
        tab.append(
          el('span', 'pz-board-sheettab-name', sheetLabel(i)),
          el('span', 'pz-board-sheettab-count', texts.sheetSlots(allBoxes.filter((b) => b.sheet === i).length)),
        );
        sheetBar.append(tab);
        sheetTabs.push(tab);
      }
      card.append(head, sheetBar, main, live);
    } else {
      card.append(head, main, live);
    }

    // One button per active slot of the sheet on the stage, in target order
    // (keyboard order = "Mesto n"); built again when another sheet is shown.
    const slotButtons = new Map<string, HTMLButtonElement>();
    const standins = new Map<string, { box: HTMLElement; flip: HTMLElement; img: HTMLImageElement; key: string }>();
    const buildSheetDom = (): void => {
      for (const button of slotButtons.values()) button.remove();
      for (const entry of standins.values()) {
        entry.img.removeAttribute('src');
        entry.box.remove();
      }
      slotButtons.clear();
      standins.clear();
      for (const box of boxes) buildSlotDom(box);
    };
    function buildSlotDom(box: SlotBox): void {
      const pos = {
        left: `${((box.cx - box.w / 2) / sceneSize.w) * 100}%`,
        top: `${((box.cy - box.h / 2) / sceneSize.h) * 100}%`,
        width: `${(box.w / sceneSize.w) * 100}%`,
        height: `${(box.h / sceneSize.h) * 100}%`,
        transform: box.rotation ? `rotate(${box.rotation}deg)` : '',
      };
      const round = box.element?.clipShape === 'ellipse';
      const button = el('button', `pz-board-slot${round ? ' is-round' : ''}`);
      button.type = 'button';
      button.setAttribute('data-el', box.id);
      Object.assign(button.style, pos);
      hitWorld.append(button);
      slotButtons.set(box.id, button);

      const standin = el('div', 'pz-board-standin is-empty');
      Object.assign(standin.style, pos);
      if (box.element?.clipShape) setMask(standin, shapeMaskUrl(box.element.clipShape));
      const flip = el('div', 'pz-board-flip');
      const t = box.element?.transform;
      if (t?.flipX || t?.flipY) flip.style.transform = `scale(${t.flipX ? -1 : 1}, ${t.flipY ? -1 : 1})`;
      const img = el('img', '');
      img.alt = '';
      img.draggable = false;
      const filter = photoCssFilter(box.element?.effects);
      if (filter) img.style.filter = filter;
      flip.append(img);
      standin.append(flip);
      standinLayer.append(standin);
      standins.set(box.id, { box: standin, flip, img, key: '' });
    }
    buildSheetDom();

    const layer = host.openLayer('pz-board', () => finish());
    layer.root.append(card);

    // ── Announcements ──────────────────────────────────────────────────────
    let announceTimer: number | undefined;
    const announce = (text: string | undefined): void => {
      if (!text) return;
      live.textContent = '';
      cancel(announceTimer);
      // Set on the next tick, so the same sentence twice is read twice.
      announceTimer = later(() => {
        live.textContent = text;
      }, 30);
    };
    let noticeTimer: number | undefined;
    const showNotice = (text: string): void => {
      if (closed || !text) return;
      notice.textContent = text;
      notice.hidden = false;
      announce(text);
      cancel(noticeTimer);
      noticeTimer = later(() => {
        notice.hidden = true;
        notice.textContent = '';
      }, NOTICE_MS);
    };
    if (typeof extras.onNotice === 'function') {
      const off = extras.onNotice(showNotice);
      if (typeof off === 'function') cleanups.push(off);
    }

    // ── Facts about the current answer ─────────────────────────────────────
    const fillOf = (id: string): CollageFill | undefined => (known ? known.slots[id] : undefined);
    const spares = (): string[] => (known ? collageSpares(known, activeIds) : []);
    const facts = {
      filled: (id: string) => !!fillOf(id),
      frameable: (id: string) => frameable(boxById.get(id)),
    };
    const thumbOf = (assetId: string): string | undefined => host.thumbUrl(assetId) ?? host.drawUrl(assetId);

    const currentScene = (): Scene => {
      if (!sceneFor || sceneFor.answer !== known) sceneFor = { answer: known, scenes: sheetScenes() };
      return sceneFor.scenes[cur] ?? sceneFor.scenes[0]!;
    };

    // ── The view ───────────────────────────────────────────────────────────
    const measureStage = (): Size => {
      const r = stage.getBoundingClientRect();
      stageRect = { left: r.left, top: r.top };
      return { w: r.width, h: r.height };
    };
    const toStagePoint = (p: GesturePoint): GesturePoint => ({ x: p.x - stageRect.left, y: p.y - stageRect.top });

    let lastApplied: BoardView | undefined;
    const applyView = (animated: boolean): void => {
      const transform = `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`;
      for (const world of [sheetWorld, hitWorld]) {
        world.classList.toggle('is-animated', animated);
        world.style.transform = transform;
        world.style.setProperty('--pzb-inv', String(1 / view.zoom));
      }
      zoomOut.disabled = view.zoom <= limits.min + 1e-3;
      zoomIn.disabled = view.zoom >= limits.max - 1e-3;
      zoomFit.disabled = view.zoom <= limits.min + 1e-3 && sameView(view, clampView(view, stageSize, sceneSize, 0));
      if (!lastApplied || !sameView(lastApplied, view)) {
        hideDetail();
        scheduleDetail();
      }
      lastApplied = { ...view };
    };
    const sizeWorlds = (): void => {
      const w = `${sceneSize.w * view.fit}px`;
      const h = `${sceneSize.h * view.fit}px`;
      for (const world of [sheetWorld, hitWorld]) {
        world.style.width = w;
        world.style.height = h;
      }
    };
    const setView = (next: BoardView, animated: boolean): void => {
      view = next;
      applyView(animated);
    };
    const stageCentre = (): GesturePoint => ({ x: stageSize.w / 2, y: stageSize.h / 2 });
    const zoomBy = (factor: number, at: GesturePoint = stageCentre(), animated = true): void => {
      setView(clampView(zoomAt(view, factor, at, limits), stageSize, sceneSize, 0), animated);
    };
    const toFit = (animated = true): void => {
      setView(fitView(stageSize, sceneSize, STAGE_PAD), animated);
    };
    const showSlot = (id: string, zoom: number, animated: boolean): void => {
      const box = boxById.get(id);
      if (!box) return;
      if (box.sheet !== cur) showSheet(box.sheet);
      setView(focusOn(view, box, zoom, stageSize, sceneSize, limits), animated);
    };

    let sheetFit = 0;
    const layout = (): boolean => {
      const size = measureStage();
      if (!(size.w > 0 && size.h > 0)) return false;
      const first = stageSize.w <= 0 || stageSize.h <= 0;
      if (first) view = fitView(size, sceneSize, STAGE_PAD);
      else if (size.w !== stageSize.w || size.h !== stageSize.h) view = refitView(view, stageSize, size, sceneSize, STAGE_PAD);
      else return true;
      stageSize = size;
      sizeWorlds();
      applyView(false);
      // A new fit big enough to matter (a phone turned) is drawn again.
      if (!sheetFit || Math.abs(view.fit / sheetFit - 1) > 0.15) scheduleSheet(0);
      return true;
    };

    // ── Rendering: the sheet, the sharp part, stand-ins ────────────────────
    let sheetGen = 0;
    let sheetTimer: number | undefined;
    let sheetWatchdog: number | undefined;
    let pendingTimer: number | undefined;

    const setStandIn = (on: boolean): void => {
      standIn = on;
      card.classList.toggle('is-standin', on);
      if (on) {
        setLoading(false);
        hideDetail();
      }
      refreshStandins();
    };
    const setLoading = (on: boolean): void => {
      loading = on;
      card.classList.toggle('is-loading', on);
      loadingNote.hidden = !on;
      if (!on) refreshStandins();
    };

    const scheduleSheet = (delay = RERENDER_DELAY_MS): void => {
      cancel(sheetTimer);
      if (rendererMissing) return;
      sheetTimer = later(() => void renderSheet(), delay);
    };

    // One render of the sheet at a time: on a phone two 2048 px surfaces in
    // the WASM heap at once is how it runs out. An edit while one is on its
    // way asks for one more when it lands (the latest answer, not each step).
    let sheetBusy = false;
    let sheetAgain = false;
    const renderSheet = async (): Promise<void> => {
      if (rendererMissing || stageSize.w <= 0) return;
      if (sheetBusy) {
        sheetAgain = true;
        return;
      }
      sheetBusy = true;
      try {
        await drawSheet();
      } finally {
        sheetBusy = false;
        if (sheetAgain && !closed) {
          sheetAgain = false;
          scheduleSheet(0);
        }
      }
    };
    const drawSheet = async (): Promise<void> => {
      const gen = ++sheetGen;
      const scale = sheetRenderScale(view, sceneSize, deps.dpr());
      const fitAtStart = view.fit;
      cancel(sheetWatchdog);
      // Nothing in 6 s (a phone busy decoding, a renderer that never came): stand-ins.
      sheetWatchdog = later(() => {
        if (gen === sheetGen && (loading || !standIn)) setStandIn(true);
      }, RENDER_TIMEOUT_MS);
      let out: CollageRender | undefined;
      try {
        out = await host.render(currentScene(), { scale });
      } catch {
        if (closed || gen !== sheetGen) return;
        cancel(sheetWatchdog);
        renderFailures++;
        // Out of WASM memory, most likely: stand-ins, and one more try on the next edit.
        if (renderFailures >= 2) rendererMissing = true;
        setStandIn(true);
        return;
      }
      if (closed || gen !== sheetGen) return;
      cancel(sheetWatchdog);
      if (!out) {
        rendererMissing = true;
        setStandIn(true);
        return;
      }
      if (!paint(sheet, out)) {
        rendererMissing = true;
        setStandIn(true);
        return;
      }
      sheetFit = fitAtStart;
      renderFailures = 0;
      if (standIn) setStandIn(false);
      if (loading) setLoading(false);
      clearPending();
      scheduleDetail();
    };

    let detailGen = 0;
    let detailTimer: number | undefined;
    const hideDetail = (): void => {
      detailGen++;
      cancel(detailTimer);
      detail.hidden = true;
    };
    const scheduleDetail = (): void => {
      cancel(detailTimer);
      if (standIn || loading || rendererMissing || gestureActive || framing) return;
      if (view.zoom < DETAIL_MIN_ZOOM) return;
      detailTimer = later(() => void renderDetail(), DETAIL_DELAY_MS);
    };
    // Also one at a time; a view that moved meanwhile gets its own once this lands.
    let detailBusy = false;
    let detailAgain = false;
    const renderDetail = async (): Promise<void> => {
      if (detailBusy) {
        detailAgain = true;
        return;
      }
      detailBusy = true;
      try {
        await drawDetail();
      } finally {
        detailBusy = false;
        if (detailAgain && !closed) {
          detailAgain = false;
          scheduleDetail();
        }
      }
    };
    const drawDetail = async (): Promise<void> => {
      const gen = ++detailGen;
      const at = { ...view };
      const rect: Rect = visibleSceneRect(at, stageSize, sceneSize);
      if (rect.w < 1 || rect.h < 1) return;
      const scale = detailRenderScale(at, rect, deps.dpr());
      let out: CollageRender | undefined;
      try {
        out = await host.render(currentScene(), { scale, viewport: rect });
      } catch {
        return;
      }
      if (closed || gen !== detailGen || !out || !sameView(at, view) || gestureActive) return;
      if (!paint(detail, out)) return;
      const s = viewScale(at);
      detail.style.width = `${rect.w * s}px`;
      detail.style.height = `${rect.h * s}px`;
      detail.style.transform = `translate(${at.x + rect.x * s}px, ${at.y + rect.y * s}px)`;
      detail.hidden = false;
    };

    /** Draws one slot's stand-in: its thumbnail placed exactly as the renderer places the photo. */
    const drawStandin = (id: string): void => {
      const entry = standins.get(id);
      const box = boxById.get(id);
      if (!entry || !box) return;
      const fill = fillOf(id);
      const key = fillKey(fill);
      if (entry.key === key) return;
      entry.key = key;
      const url = fill ? thumbOf(fill.assetId) : undefined;
      entry.box.classList.toggle('is-empty', !fill || !url);
      if (!fill || !url) {
        entry.img.removeAttribute('src');
        return;
      }
      const photo = photoOf(known, fill.assetId);
      const img = entry.img;
      if (photo?.w && photo.h) {
        entry.box.classList.remove('is-cover');
        const size = { w: box.w, h: box.h };
        const place = photoPlacement({ w: photo.w, h: photo.h }, size, fill.crop);
        img.style.left = `${(place.x / size.w) * 100}%`;
        img.style.top = `${(place.y / size.h) * 100}%`;
        img.style.width = `${(place.w / size.w) * 100}%`;
        img.style.height = `${(place.h / size.h) * 100}%`;
        img.style.transformOrigin = `${((size.w / 2 - place.x) / place.w) * 100}% ${((size.h / 2 - place.y) / place.h) * 100}%`;
        img.style.transform = place.rotation ? `rotate(${place.rotation}deg)` : '';
      } else {
        // No size yet (an odd format): the renderer's own default, a centred cover.
        entry.box.classList.add('is-cover');
        for (const prop of ['left', 'top', 'width', 'height', 'transform', 'transformOrigin'] as const) img.style[prop] = '';
      }
      if (img.getAttribute('src') !== url) img.src = url;
    };
    /** Stand-ins are drawn only while they show: loading, stand-in mode, or pending over a changed slot. */
    const refreshStandins = (): void => {
      for (const id of activeIds) {
        const entry = standins.get(id);
        if (!entry) continue;
        if (standIn || loading || entry.box.classList.contains('is-pending')) drawStandin(id);
      }
    };
    const markPending = (ids: readonly string[]): void => {
      if (ids.length === 0 || standIn || loading) return;
      for (const id of ids) {
        const entry = standins.get(id);
        if (!entry) continue;
        entry.box.classList.add('is-pending');
        drawStandin(id);
      }
      cancel(pendingTimer);
      pendingTimer = later(clearPending, PENDING_MAX_MS);
    };
    const clearPending = (): void => {
      cancel(pendingTimer);
      pendingTimer = undefined;
      for (const entry of standins.values()) entry.box.classList.remove('is-pending', 'is-flip');
    };

    /** A swap's FLIP: the stand-in in `to` starts where its photo was (`from`) and slides home. */
    const flipFrom = (to: string, from: string): void => {
      const entry = standins.get(to);
      const a = boxById.get(to);
      const b = boxById.get(from);
      if (!entry || !a || !b || standIn || loading || a.sheet !== b.sheet) return;
      const dx = (b.cx - a.cx) * view.fit;
      const dy = (b.cy - a.cy) * view.fit;
      const rest = a.rotation ? `rotate(${a.rotation}deg)` : '';
      entry.box.classList.remove('is-flip');
      entry.box.style.transform = `translate(${dx}px, ${dy}px) ${rest}`.trim();
      deps.raf(() =>
        deps.raf(() => {
          if (closed) return;
          entry.box.classList.add('is-flip');
          entry.box.style.transform = rest;
        }),
      );
      later(() => entry.box.classList.remove('is-flip'), FLIP_MS + 40);
    };

    // ── The answer changed (here, by undo, or by the host) ─────────────────
    const changedSlots = (before: CollageAnswer | null, after: CollageAnswer | null): string[] =>
      activeIds.filter((id) => fillKey(before?.slots[id]) !== fillKey(after?.slots[id]));

    const answerChanged = (next: CollageAnswer | null, flips: Array<{ to: string; from: string }> = []): void => {
      const before = known;
      known = next;
      // Only the sheet on the stage is drawn again; another one is drawn when shown.
      const changed = changedSlots(before, next).filter((id) => boxById.get(id)?.sheet === cur);
      if (changed.length > 0) {
        hideDetail();
        markPending(changed);
        for (const f of flips) flipFrom(f.to, f.from);
        if (standIn || loading) refreshStandins();
        scheduleSheet();
      }
      // The selection must still point at something.
      if (selection?.kind === 'spare' && !spares().includes(selection.id)) selection = null;
      refresh();
    };

    const commit = (next: CollageAnswer, announcement?: string, flips: Array<{ to: string; from: string }> = []): void => {
      if (!known || next === known) return;
      host.commit(next);
      answerChanged(host.answer() ?? next, flips);
      announce(announcement);
    };

    /**
     * Brings `known` up to the host's answer. `known` is refreshed by the
     * watch only every WATCH_MS, and a photo can land in between (the board's
     * own "Dodaj slike" adds photos while it is open): every action that
     * changes the answer catches up first and builds on the host's answer, or
     * its command would be made from an older one and drop the new photo.
     * Synchronous from here to `host.commit`, so nothing lands in between.
     */
    const catchUp = (): void => {
      const now = host.answer();
      if (now !== known) answerChanged(now);
    };
    /** The selection still names something there: an active slot, or a photo still in "Rezerva". */
    const stillThere = (target: BoardSelection): boolean =>
      target.kind === 'slot' ? boxById.has(target.id) : spares().includes(target.id);

    // ── What the shopper does ──────────────────────────────────────────────
    const select = (next: BoardSelection | null): void => {
      if (sameSelection(selection, next) && !swapping) return;
      selection = next;
      swapping = false;
      refresh();
    };

    const startSwap = (): void => {
      if (!selection || !stillThere(selection)) return;
      swapping = true;
      refresh();
      announce(mouse ? texts.hintSwapMouse : texts.hintSwap);
    };

    const exchange = (a: BoardSelection, b: BoardSelection): void => {
      selection = null;
      swapping = false;
      catchUp();
      if (!known || !stillThere(a) || !stillThere(b)) {
        refresh();
        return;
      }
      const next = exchangeCollage(known, refOf(a), refOf(b), ctx);
      if (next === known) {
        refresh();
        return;
      }
      const slotA = a.kind === 'slot' ? a.id : undefined;
      const slotB = b.kind === 'slot' ? b.id : undefined;
      const filledA = slotA ? !!fillOf(slotA) : true;
      const filledB = slotB ? !!fillOf(slotB) : true;
      const flips: Array<{ to: string; from: string }> = [];
      if (slotA && slotB) {
        if (filledB) flips.push({ to: slotA, from: slotB });
        if (filledA) flips.push({ to: slotB, from: slotA });
      }
      // A hand framing that came along from a slot of other proportions or
      // outline kept its centre but may want a look ("Proveri kadar").
      if (slotA && slotB) {
        for (const [from, to] of [[slotA, slotB], [slotB, slotA]] as const) {
          const moved = fillOf(from);
          const fromBox = boxById.get(from);
          const toBox = boxById.get(to);
          recheck.delete(to);
          if (moved?.manual && fromBox && toBox && !sameCollageFrame(fromBox.geom, toBox.geom)) recheck.add(to);
        }
      }
      // Both hold a photo: they swap places. One side empty: a photo moved.
      const text = filledA && filledB ? texts.swapped : texts.moved;
      commit(next, text, flips);
      showNotice(`${text} ${mouse ? texts.undoHintMouse : texts.undoHint}`);
    };

    const removeSelected = (): void => {
      catchUp();
      if (!known || !selection || !stillThere(selection)) return;
      const next =
        selection.kind === 'slot' ? removeFromSlot(known, selection.id, ctx) : removeCollagePhoto(known, selection.id);
      if (next === known) return;
      if (selection.kind === 'slot') recheck.delete(selection.id);
      selection = null;
      swapping = false;
      commit(next, texts.removed);
    };

    const resetSelected = (): void => {
      catchUp();
      if (!known || selection?.kind !== 'slot') return;
      recheck.delete(selection.id);
      const next = resetSlotCrop(known, selection.id, ctx);
      commit(next, texts.autoReset);
    };

    const shuffle = (): void => {
      catchUp();
      if (!known) return;
      selection = null;
      swapping = false;
      recheck.clear();
      commit(shuffleCollage(known, randomCollageSeed(deps.random), ctx), texts.shuffled);
    };

    const repeatPhotos = (): void => {
      catchUp();
      if (!known) return;
      selection = null;
      swapping = false;
      commit(fillRepeats(known, known.seed ?? randomCollageSeed(deps.random), ctx), texts.repeated);
    };

    const undo = (): void => {
      if (!host.undo()) return;
      selection = null;
      swapping = false;
      recheck.clear();
      answerChanged(host.answer());
      announce(texts.undone);
    };

    const step = (direction: -1 | 1): void => {
      if (selection?.kind !== 'slot' || allBoxes.length === 0) return;
      const at = allBoxes.findIndex((b) => b.id === selection!.id);
      const next = allBoxes[(at + direction + allBoxes.length) % allBoxes.length]!;
      selection = { kind: 'slot', id: next.id };
      swapping = false;
      const zoom = view.zoom;
      // The next place may be on another sheet: the stage follows it.
      if (next.sheet !== cur) showSheet(next.sheet);
      if (zoom > limits.min + 1e-3) showSlot(next.id, zoom, true);
      refresh();
    };

    /**
     * Puts sheet `index` on the stage: its slots drawn and hit, the view at
     * fit, its picture rendered. The selection stays, so a photo chosen on
     * one sheet swaps with a place tapped on the other.
     */
    function showSheet(index: number): void {
      if (!multi || closed || index === cur || index < 0 || index >= sheetCount) return;
      cur = index;
      hideDetail();
      // A picture of the other sheet still on its way is not painted here.
      sheetGen++;
      cancel(sheetTimer);
      cancel(sheetWatchdog);
      clearPending();
      const scene = currentScene();
      sceneSize = { w: scene.width, h: scene.height };
      boxes = allBoxes.filter((b) => b.sheet === cur);
      buildSheetDom();
      sheet.width = 0;
      sheet.height = 0;
      sheetFit = 0;
      if (stageSize.w > 0 && stageSize.h > 0) {
        view = fitView(stageSize, sceneSize, STAGE_PAD);
        sizeWorlds();
        applyView(false);
      }
      if (!rendererMissing) setLoading(true);
      refreshStandins();
      refresh();
      scheduleSheet(0);
    }

    const frame = async (id: string): Promise<void> => {
      if (framing || !known) return;
      const fill = known.slots[id];
      const box = boxById.get(id);
      if (!fill || !box || !frameable(box)) return;
      const url = host.framerUrl(fill.assetId) ?? host.drawUrl(fill.assetId) ?? host.thumbUrl(fill.assetId);
      if (!url) return;
      framing = true;
      hideDetail();
      resetGestures();
      try {
        const photo = photoOf(known, fill.assetId);
        const source = photo?.w && photo.h ? { w: photo.w, h: photo.h } : await naturalSize(url);
        if (!source || closed) return;
        // Framed against the photo's own proportions (the framer's picture has the same ones).
        const auto =
          collageAutoCrop({ ...(photo?.faces ? { faces: photo.faces } : {}), ...source }, box.geom, { faceFraming: rules.faceFraming })
            .crop ?? { cx: 0.5, cy: 0.5, zoom: 1 };
        const look = await lookOf(box);
        const framed = await deps.openFramer({
          host: layerHostOf(card),
          title: texts.framerTitle(box.n),
          imageUrl: url,
          source,
          aspect: box.w / box.h,
          crop: fill.crop ?? auto,
          rotateStep: turnStepOf(box.element ?? {}, false),
          cutout: false,
          ...look,
          clampZoom: (zoom: number) => Math.min(Math.max(zoom, 1), COLLAGE_MAX_ZOOM),
          resetCrop: auto,
          resetLabel: texts.framerReset,
        });
        // Looked at: whatever came of it, the slot needs no "Proveri kadar".
        recheck.delete(id);
        if (!framed || closed) return;
        // Photos may have landed while the framer was open (the watch pauses):
        // catch up first, and frame only if the slot still holds this photo.
        const now = host.answer();
        if (now !== known) answerChanged(now);
        if (!now || now.slots[id]?.assetId !== fill.assetId) return;
        const next = setSlotCrop(now, id, framed, ctx);
        if (next === now) return;
        const backToAuto = now.slots[id]?.manual === true && next.slots[id]?.manual !== true;
        commit(next, backToAuto ? texts.autoReset : undefined);
      } finally {
        framing = false;
        if (!closed) {
          // Done with this photo: nothing stays chosen, so the next tap on
          // another photo just picks that one.
          selection = null;
          swapping = false;
          refresh();
          scheduleDetail();
        }
      }
    };

    /** The framer's mask, clip shape, colour effects and mirroring, as on the product (canvas-edit's `standInLook`). */
    const lookOf = async (box: SlotBox): Promise<Partial<FramerOptions>> => {
      const element = box.element;
      if (!element) return {};
      const out: Partial<FramerOptions> = {};
      const filter = photoCssFilter(element.effects);
      if (filter) out.filter = filter;
      if (element.clipShape) out.shapeMask = shapeMaskUrl(element.clipShape);
      if (element.transform.flipX) out.flipX = true;
      if (element.transform.flipY) out.flipY = true;
      if (element.mask) {
        // The §7.1 host has no general asset lookup: an optional `assetUrl` when
        // the host offers one, else `drawUrl`, which falls back to the page's
        // own asset URLs for ids it did not upload (a template's mask is one).
        const id = element.mask.assetId;
        const raw =
          (typeof extras.assetUrl === 'function' ? extras.assetUrl(id) : undefined) ??
          host.drawUrl(id) ??
          (/^(https?:|data:|blob:)/i.test(id) ? id : undefined);
        const mask = raw && element.mask.mode === 'black-white' ? await blackWhiteMask(raw) : raw;
        if (mask) out.maskUrl = mask;
      }
      return out;
    };

    const tap = (target: HitTarget): void => {
      // Decided on what is there now: a slot that just got its photo is not "empty".
      catchUp();
      const decision = decideTap(selection, target, facts, swapping);
      switch (decision.type) {
        case 'select':
          select(decision.target);
          break;
        case 'deselect':
          select(null);
          break;
        case 'frame':
          void frame(decision.slot);
          break;
        case 'exchange':
          exchange(decision.a, decision.b);
          break;
        default:
          break;
      }
    };

    const toggleZoom = (point: GesturePoint): void => {
      if (view.zoom > limits.min + 0.05) toFit(true);
      else setView(clampView(zoomAt(view, DOUBLE_TAP_ZOOM / view.zoom, point, limits), stageSize, sceneSize, 0), true);
    };

    // ── Gestures ───────────────────────────────────────────────────────────
    const machine = new GestureMachine({
      liftable: (target) =>
        (target.kind === 'slot' && !!fillOf(target.id)) || (target.kind === 'spare' && spares().includes(target.id)),
    });
    let longPressTimer: number | undefined;
    let drag:
      | { from: HitTarget; point: GesturePoint; hover?: HTMLElement; frame?: number }
      | undefined;

    const hitAt = (client: GesturePoint): HitTarget | undefined => {
      for (const button of spareButtons.values()) {
        const r = button.getBoundingClientRect();
        if (client.x >= r.left && client.x <= r.right && client.y >= r.top && client.y <= r.bottom) {
          return { kind: 'spare', id: button.getAttribute('data-spare') ?? '' };
        }
      }
      const r = stage.getBoundingClientRect();
      if (client.x < r.left || client.x > r.right || client.y < r.top || client.y > r.bottom) return undefined;
      const box = slotAt(view, { x: client.x - r.left, y: client.y - r.top }, boxes);
      return box ? { kind: 'slot', id: box.id } : { kind: 'background' };
    };
    const buttonFor = (target: HitTarget | undefined): HTMLElement | undefined =>
      target?.kind === 'slot' ? slotButtons.get(target.id) : target?.kind === 'spare' ? spareButtons.get(target.id) : undefined;

    const moveGhost = (client: GesturePoint): void => {
      const p = toStagePoint(client);
      ghost.style.transform = `translate(${p.x - 36}px, ${p.y - 84}px)`;
    };
    const startDrag = (from: HitTarget, point: GesturePoint, pointer: PointerKind): void => {
      const assetId = from.kind === 'slot' ? fillOf(from.id)?.assetId : from.kind === 'spare' ? from.id : undefined;
      if (!assetId) return;
      if (pointer !== 'mouse') deps.vibrate(12);
      selection = null;
      swapping = false;
      refresh();
      const url = thumbOf(assetId);
      ghost.style.backgroundImage = url ? `url("${url.replace(/"/g, '%22')}")` : '';
      moveGhost(point);
      ghost.hidden = false;
      card.classList.add('is-dragging');
      buttonFor(from)?.classList.add('is-lifted');
      drag = { from, point };
      const tick = (): void => {
        if (!drag || closed) return;
        autoPan(drag.point);
        drag.frame = deps.raf(tick);
      };
      drag.frame = deps.raf(tick);
    };
    /** The sheet tab under a point on screen, if any. */
    const sheetTabAt = (client: GesturePoint): number | undefined => {
      for (let i = 0; i < sheetTabs.length; i++) {
        const r = sheetTabs[i]!.getBoundingClientRect();
        if (r.width > 0 && client.x >= r.left && client.x <= r.right && client.y >= r.top && client.y <= r.bottom) return i;
      }
      return undefined;
    };
    const hoverDrag = (client: GesturePoint): void => {
      if (!drag) return;
      drag.point = client;
      moveGhost(client);
      // Carried over another sheet's tab: that sheet comes on the stage, and
      // the photo can be dropped on one of its places.
      const tab = multi ? sheetTabAt(client) : undefined;
      if (tab !== undefined && tab !== cur) {
        drag.hover?.classList.remove('is-drop-hover');
        drag.hover = undefined;
        showSheet(tab);
      }
      const target = hitAt(client);
      const over = target && !sameTarget(target, drag.from) ? buttonFor(target) : undefined;
      if (over !== drag.hover) {
        drag.hover?.classList.remove('is-drop-hover');
        over?.classList.add('is-drop-hover');
        drag.hover = over;
      }
    };
    /** Near an edge of the stage, a zoomed sheet slides so the drop target can be reached. */
    const autoPan = (client: GesturePoint): void => {
      if (view.zoom <= limits.min + 1e-3) return;
      const p = toStagePoint(client);
      const push = (d: number): number => (d < EDGE ? ((EDGE - Math.max(0, d)) / EDGE) * EDGE_SPEED : 0);
      const dx = push(p.x) - push(stageSize.w - p.x);
      const dy = push(p.y) - push(stageSize.h - p.y);
      if (!dx && !dy) return;
      const next = clampView(panBy(view, dx, dy), stageSize, sceneSize, 0);
      if (!sameView(next, view, 0.01)) setView(next, false);
    };
    const endDrag = (): void => {
      if (!drag) return;
      deps.cancelRaf(drag.frame);
      drag.hover?.classList.remove('is-drop-hover');
      buttonFor(drag.from)?.classList.remove('is-lifted');
      drag = undefined;
      ghost.hidden = true;
      ghost.style.backgroundImage = '';
      card.classList.remove('is-dragging');
    };
    const dropAt = (from: HitTarget, client: GesturePoint): void => {
      endDrag();
      const target = hitAt(client);
      if (!target || target.kind === 'background' || from.kind === 'background') return;
      if (target.kind === from.kind && target.id === from.id) return;
      const a: BoardSelection = { kind: from.kind, id: from.id };
      const b: BoardSelection = { kind: target.kind, id: target.id };
      if (a.kind === 'spare' && b.kind === 'spare') return;
      exchange(a, b);
    };

    const run = (actions: GestureAction[]): void => {
      for (const action of actions) {
        switch (action.type) {
          case 'tap':
            tap(action.target);
            break;
          case 'doubleTap':
            toggleZoom(toStagePoint(action.point));
            break;
          case 'armLongPress': {
            cancel(longPressTimer);
            const id = action.pointerId;
            longPressTimer = later(() => run(machine.longPress(id, deps.now())), action.ms);
            break;
          }
          case 'disarmLongPress':
            cancel(longPressTimer);
            longPressTimer = undefined;
            break;
          case 'gestureStart':
            gestureActive = true;
            hideDetail();
            for (const world of [sheetWorld, hitWorld]) world.classList.remove('is-animated');
            break;
          case 'gestureEnd':
            gestureActive = false;
            stage.classList.remove('is-panning');
            setView(clampView(view, stageSize, sceneSize, 0), true);
            scheduleDetail();
            break;
          case 'pan':
            if (pressOrigin === 'strip') break;
            stage.classList.add('is-panning');
            setView(clampView(panBy(view, action.dx, action.dy), stageSize, sceneSize, OVERSCROLL), false);
            break;
          case 'pinch': {
            if (pressOrigin === 'strip') break;
            const zoomed = zoomAt(view, action.factor, toStagePoint(action.center), limits);
            setView(clampView(panBy(zoomed, action.dx, action.dy), stageSize, sceneSize, OVERSCROLL), false);
            break;
          }
          case 'lift':
            startDrag(action.from, action.point, action.pointer);
            break;
          case 'dragMove':
            hoverDrag(action.point);
            break;
          case 'drop':
            dropAt(action.from, action.point);
            break;
          case 'dragCancel':
            endDrag();
            break;
          default:
            break;
        }
      }
    };
    const resetGestures = (): void => {
      run(machine.reset());
      endDrag();
      gestureActive = false;
    };

    const pointerKind = (event: PointerEvent): PointerKind =>
      event.pointerType === 'mouse' ? 'mouse' : event.pointerType === 'pen' ? 'pen' : 'touch';
    const client = (event: PointerEvent): GesturePoint => ({ x: event.clientX, y: event.clientY });
    const capture = (node: HTMLElement, event: PointerEvent): void => {
      try {
        node.setPointerCapture?.(event.pointerId);
      } catch {
        /* synthetic pointers have no capturable id */
      }
    };

    listen(stage, 'pointerdown', (event: PointerEvent) => {
      if (framing || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();
      lastPointerAt = deps.now();
      if (!gestureActive && !machine.dragging) pressOrigin = 'stage';
      measureStage();
      const p = client(event);
      const box = slotAt(view, toStagePoint(p), boxes);
      const target: HitTarget = box ? { kind: 'slot', id: box.id } : { kind: 'background' };
      capture(stage, event);
      run(machine.down(event.pointerId, pointerKind(event), p, target, deps.now()));
      // Keys (Escape, Ctrl+Z, zoom) keep working after a tap on the sheet.
      if (typeof stage.focus === 'function' && !isInside(activeElementOf(card), stage)) {
        try {
          stage.focus({ preventScroll: true });
        } catch {
          /* focus is a nicety */
        }
      }
    });
    listen(strip, 'pointerdown', (event: PointerEvent) => {
      if (framing || (event.pointerType === 'mouse' && event.button !== 0)) return;
      const spareEl = closestWith(event.target, 'data-spare', strip);
      if (!spareEl) return;
      lastPointerAt = deps.now();
      pressOrigin = 'strip';
      measureStage();
      capture(strip, event);
      run(machine.down(event.pointerId, pointerKind(event), client(event), { kind: 'spare', id: spareEl.getAttribute('data-spare') ?? '' }, deps.now()));
    });
    listen(main, 'pointermove', (event: PointerEvent) => {
      run(machine.move(event.pointerId, client(event)));
    });
    listen(main, 'pointerup', (event: PointerEvent) => {
      lastPointerAt = deps.now();
      run(machine.up(event.pointerId, client(event), deps.now()));
    });
    const lost = (event: PointerEvent): void => {
      run(machine.cancel(event.pointerId));
    };
    listen(main, 'pointercancel', lost);
    listen(main, 'lostpointercapture', lost);
    listen(
      stage,
      'wheel',
      (event: WheelEvent) => {
        event.preventDefault();
        measureStage();
        const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? stageSize.h : 1;
        const rate = event.ctrlKey ? 0.01 : 0.0015;
        const factor = Math.exp(-event.deltaY * scale * rate);
        setView(clampView(zoomAt(view, factor, toStagePoint(client(event as unknown as PointerEvent)), limits), stageSize, sceneSize, 0), false);
      },
      { passive: false },
    );
    // iOS Safari's own page zoom would take a pinch; a long press would open
    // the image menu or select text.
    const swallow = (event: Event): void => {
      if (event.cancelable) event.preventDefault();
    };
    for (const type of ['gesturestart', 'gesturechange', 'gestureend']) {
      listen(stage, type, swallow, { passive: false });
      listen(strip, type, swallow, { passive: false });
    }
    listen(stage, 'contextmenu', swallow);
    listen(strip, 'contextmenu', swallow);

    // Keyboard and assistive technology: the slot and spare buttons act on
    // click. A click that follows a pointer press was already handled by the
    // gesture machine and is ignored.
    const fromPointer = (): boolean => deps.now() - lastPointerAt < 800;
    listen(hitWorld, 'click', (event: MouseEvent) => {
      const button = closestWith(event.target, 'data-el', hitWorld);
      if (!button || fromPointer() || framing) return;
      tap({ kind: 'slot', id: button.getAttribute('data-el') ?? '' });
    });
    listen(strip, 'click', (event: MouseEvent) => {
      const button = closestWith(event.target, 'data-spare', strip);
      if (!button || fromPointer() || framing) return;
      tap({ kind: 'spare', id: button.getAttribute('data-spare') ?? '' });
    });

    listen(card, 'keydown', (event: KeyboardEvent) => {
      if (framing || closed) return;
      const key = event.key;
      if (key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (drag) resetGestures();
        else if (swapping) select(selection);
        else if (selection) select(null);
        else close();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && key.toLowerCase() === 'z') {
        event.preventDefault();
        undo();
        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (key === 'Delete' || key === 'Backspace') {
        if (!selection) return;
        event.preventDefault();
        removeSelected();
      } else if (key === '+' || key === '=') {
        event.preventDefault();
        zoomBy(ZOOM_STEP);
      } else if (key === '-' || key === '_') {
        event.preventDefault();
        zoomBy(1 / ZOOM_STEP);
      } else if (key === '0') {
        event.preventDefault();
        toFit(true);
      } else if (multi && (key === 'PageDown' || key === 'PageUp')) {
        event.preventDefault();
        showSheet(cur + (key === 'PageDown' ? 1 : -1));
      }
    });

    // A clicked control that has just become disabled ("Cela strana" once the
    // page is whole, a zoom button at its limit) or was rebuilt drops focus to
    // the dialog, and Safari never focuses a clicked button at all: Escape
    // then reached the dialog and closed the whole board instead of first
    // clearing the selection. Keys come back to the stage.
    listen(card, 'click', () => {
      later(() => {
        if (framing) return;
        const active = activeElementOf(card) as (Element & { disabled?: boolean }) | null;
        if (active && isInside(active, card) && active.disabled !== true) return;
        try {
          stage.focus?.({ preventScroll: true });
        } catch {
          /* focus is a nicety */
        }
      }, 0);
    });

    const close = (): void => {
      layer.close();
      finish();
    };
    listen(done, 'click', close);
    if (sheetBar) {
      listen(sheetBar, 'click', (event: MouseEvent) => {
        const tab = closestWith(event.target, 'data-sheet', sheetBar);
        if (!tab || framing) return;
        showSheet(Number(tab.getAttribute('data-sheet')));
      });
    }
    listen(zoomIn, 'click', () => zoomBy(ZOOM_STEP));
    listen(zoomOut, 'click', () => zoomBy(1 / ZOOM_STEP));
    listen(zoomFit, 'click', () => toFit(true));

    // The action bar: one delegated listener; buttons are rebuilt with the state.
    listen(bar, 'click', (event: MouseEvent) => {
      const button = closestWith(event.target, 'data-action', bar);
      if (!button || framing || (button as HTMLButtonElement).disabled) return;
      const action = button.getAttribute('data-action');
      const slot = selection?.kind === 'slot' ? selection.id : undefined;
      switch (action) {
        case 'undo':
          undo();
          break;
        case 'shuffle':
          shuffle();
          break;
        case 'add':
          host.pickPhotos();
          break;
        case 'prev':
          step(-1);
          break;
        case 'next':
          step(1);
          break;
        case 'frame':
          if (slot) void frame(slot);
          break;
        case 'swap':
          startSwap();
          break;
        case 'cancel-swap':
          select(selection);
          break;
        case 'auto':
          resetSelected();
          break;
        case 'replace':
        case 'add-here':
          if (slot) host.pickPhotos({ elementId: slot });
          break;
        case 'remove':
        case 'drop-spare':
          removeSelected();
          break;
        case 'repeat':
          repeatPhotos();
          break;
        default:
          break;
      }
    });

    // ── The state on screen ────────────────────────────────────────────────
    const refreshSlots = (): void => {
      const advice = new Map(boxes.map((b) => [b.id, fillOf(b.id) ? host.slotAdvice(b.id) : undefined] as const));
      for (const box of boxes) {
        const button = slotButtons.get(box.id)!;
        const fill = fillOf(box.id);
        const tips = advice.get(box.id);
        const selected = selection?.kind === 'slot' && selection.id === box.id;
        button.classList.toggle('is-empty', !fill);
        button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        const words = [texts.slot(box.n, total)];
        if (!fill) words.push(texts.badgeEmpty);
        if (tips?.facesCut) words.push(texts.badgeFace);
        if (tips && tips.repeat > 1) words.push(texts.badgeRepeat);
        if (tips?.soft) words.push(PHOTO_TEXTS.warn);
        const check = !!fill && recheck.has(box.id);
        if (check) words.push(texts.badgeCheck);
        button.setAttribute('aria-label', words.join(', '));
        const key = `${fill ? 'f' : 'e'}|${tips?.facesCut ? 1 : 0}|${tips?.repeat ?? 0}|${tips?.soft ? 1 : 0}|${check ? 1 : 0}`;
        if (button.getAttribute('data-look') === key) continue;
        button.setAttribute('data-look', key);
        button.innerHTML = '';
        if (!fill) {
          const plus = el('span', 'pz-board-plus');
          plus.append(el('b', '', '+'), el('span', '', String(box.n)));
          button.append(plus);
          continue;
        }
        const badges = el('span', 'pz-board-badges');
        if (tips?.facesCut) {
          const face = el('span', 'pz-board-badge is-face');
          face.innerHTML = ICONS.face;
          face.title = texts.badgeFace;
          badges.append(face);
        }
        if (tips && tips.repeat > 1) {
          const repeat = el('span', 'pz-board-badge is-repeat', `${tips.repeat}×`);
          repeat.title = texts.badgeRepeat;
          badges.append(repeat);
        }
        if (check) {
          const look = el('span', 'pz-board-badge is-check');
          look.innerHTML = ICONS.frame.replace(/width="18" height="18"/, 'width="12" height="12"');
          look.title = texts.badgeCheck;
          badges.append(look);
        }
        if (tips?.soft) {
          const soft = el('span', 'pz-board-badge is-soft', 'i');
          soft.title = PHOTO_TEXTS.warn;
          badges.append(soft);
        }
        if (badges.children.length > 0) button.append(badges);
      }
    };

    const refreshSpares = (): void => {
      const list = spares();
      strip.hidden = list.length === 0;
      const keep = new Set(list);
      for (const [id, button] of spareButtons) {
        if (keep.has(id)) continue;
        button.remove();
        spareButtons.delete(id);
      }
      list.forEach((id, i) => {
        let button = spareButtons.get(id);
        if (!button) {
          button = el('button', 'pz-board-spare');
          button.type = 'button';
          button.setAttribute('data-spare', id);
          const url = thumbOf(id);
          if (url) button.style.backgroundImage = `url("${url.replace(/"/g, '%22')}")`;
          spareButtons.set(id, button);
        }
        // Pool order, after the title.
        strip.append(button);
        button.setAttribute('aria-label', texts.spare(i + 1));
        button.setAttribute('aria-pressed', selection?.kind === 'spare' && selection.id === id ? 'true' : 'false');
      });
    };

    const refreshBar = (): void => {
      const focusedAction = (() => {
        const active = activeElementOf(card);
        return active && isInside(active, bar) ? (active as HTMLElement).getAttribute?.('data-action') : null;
      })();
      bar.innerHTML = '';
      const info = el('div', 'pz-board-info');
      const hint = el('p', 'pz-board-hint');
      const actions = el('div', 'pz-board-actions');
      const nav = (): void => {
        info.append(
          iconButton(texts.prev, ICONS.prev, 'pz-board-nav', 'prev'),
          iconButton(texts.next, ICONS.next, 'pz-board-nav', 'next'),
        );
      };
      if (selection?.kind === 'slot') {
        const box = boxById.get(selection.id);
        const fill = fillOf(selection.id);
        const n = box?.n ?? 0;
        // Several sheets: which one the place is on ("Mesto 7 od 24 · List 2").
        const place = multi && box ? `${texts.slot(n, total)} · ${sheetLabel(box.sheet)}` : texts.slot(n, total);
        if (fill && swapping) {
          info.append(el('span', '', place));
          actions.append(actionButton(texts.cancelSwap, ICONS.cancel, 'cancel-swap'));
          hint.textContent = mouse ? texts.hintSwapMouse : texts.hintSwap;
          hint.classList.add('is-swap');
          bar.append(info, hint, actions);
        } else if (fill) {
          info.append(el('span', '', place));
          nav();
          if (frameable(box)) actions.append(actionButton(texts.frame, ICONS.frame, 'frame'));
          const others = total > 1 || spares().length > 0;
          if (others) actions.append(actionButton(texts.swap, ICONS.swap, 'swap'));
          actions.append(actionButton(texts.replace, ICONS.replace, 'replace'), actionButton(texts.remove, ICONS.remove, 'remove'));
          // Back to the automatic framing: only when there is a hand framing to undo
          // (the framer has its own "Automatski" too).
          if (fill.manual === true) actions.append(actionButton(texts.auto, ICONS.auto, 'auto'));
          hint.textContent = mouse ? texts.hintSelectedMouse : texts.hintSelected;
          const tips = host.slotAdvice(selection.id);
          bar.append(info, actions, hint);
          if (recheck.has(selection.id)) bar.append(el('p', 'pz-board-advice', texts.checkLine));
          if (tips.facesCut) bar.append(el('p', 'pz-board-advice', texts.faceLine));
          if (tips.soft) bar.append(el('p', 'pz-board-advice', PHOTO_TEXTS.warn));
        } else {
          info.append(el('span', '', `${place} — ${texts.slotEmpty}`));
          nav();
          actions.append(actionButton(texts.addHere, ICONS.add, 'add-here'));
          if (rules.allowRepeats && (known?.photos.length ?? 0) > 0) actions.append(actionButton(texts.repeat, ICONS.repeat, 'repeat'));
          hint.textContent = texts.hintEmpty;
          bar.append(info, actions, hint);
        }
      } else if (selection?.kind === 'spare') {
        hint.textContent = mouse ? texts.hintSpareMouse : texts.hintSpare;
        actions.append(actionButton(texts.dropSpare, ICONS.remove, 'drop-spare'));
        bar.append(hint, actions);
      } else {
        hint.textContent = mouse ? texts.hintMouse : texts.hintTouch;
        const undoButton = actionButton(texts.undo, ICONS.undo, 'undo');
        undoButton.disabled = !host.canUndo();
        const shuffleButton = actionButton(texts.shuffle, ICONS.shuffle, 'shuffle');
        const filled = known ? activeIds.filter((id) => known!.slots[id]).length : 0;
        shuffleButton.disabled = filled < 2;
        actions.append(undoButton, shuffleButton, actionButton(texts.add, ICONS.add, 'add'));
        bar.append(hint, actions);
      }
      holdBarHeight();
      if (focusedAction) {
        const again = descendants(bar).find((node) => node.getAttribute('data-action') === focusedAction);
        const target = again && !(again as HTMLButtonElement).disabled ? again : stage;
        try {
          target.focus?.({ preventScroll: true });
        } catch {
          /* focus is a nicety */
        }
      }
    };

    // The bar never gets shorter while the board is open. Its rows change with
    // what is tapped (a photo's place, Uokviri / Zameni / Ukloni, advice), and a
    // bar that grew and shrank resized the stage under it: the sheet was fitted
    // again, a little smaller and back, on every tap — a zoom that did nothing.
    let barReserve = 0;
    const holdBarHeight = (): void => {
      const height = bar.offsetHeight;
      if (typeof height !== 'number' || height <= barReserve) return;
      barReserve = height;
      bar.style.minHeight = `${height}px`;
    };
    /** Before the sheet is first fitted: the bar as tall as a tapped photo makes it. */
    let barReserved = false;
    const reserveBar = (): void => {
      if (barReserved || !(bar.offsetHeight > 0)) return;
      barReserved = true;
      const first = activeIds.find((id) => fillOf(id));
      if (!first || selection) return;
      selection = { kind: 'slot', id: first };
      refreshBar();
      selection = null;
      refreshBar();
    };

    /** The switcher: which sheet is on the stage, which still has empty places. */
    const refreshSheetTabs = (): void => {
      sheetTabs.forEach((tab, i) => {
        const onSheet = allBoxes.filter((b) => b.sheet === i);
        const empty = onSheet.filter((b) => !fillOf(b.id)).length;
        tab.setAttribute('aria-pressed', i === cur ? 'true' : 'false');
        tab.classList.toggle('is-active', i === cur);
        tab.classList.toggle('has-empty', empty > 0);
        const words = [sheetFullName(i), texts.sheetSlots(onSheet.length)];
        if (empty > 0) words.push(texts.sheetEmpty(empty));
        tab.setAttribute('aria-label', words.join(', '));
      });
    };

    let undoShown: boolean | undefined;
    const refresh = (): void => {
      if (closed) return;
      card.classList.toggle('is-swapping', swapping && !!selection);
      const filled = known ? activeIds.filter((id) => known!.slots[id]).length : 0;
      count.textContent = `${filled}/${total}`;
      count.setAttribute('aria-label', texts.count(filled, total));
      count.classList.toggle('is-complete', total > 0 && filled === total);
      refreshSheetTabs();
      refreshSlots();
      refreshSpares();
      refreshBar();
      undoShown = host.canUndo();
    };

    // Answers the board did not make: photos landing, the picker, the step.
    const watch = deps.setInterval(() => {
      if (closed || framing) return;
      const now = host.answer();
      if (now !== known) {
        answerChanged(now);
        return;
      }
      if (!selection && host.canUndo() !== undoShown) refresh();
    }, WATCH_MS);

    // The stage's size: a phone turned, a window resized, the dialog laid out.
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        if (!closed) layout();
      });
      resizeObserver.observe(stage);
    }
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      listen(window, 'resize', () => layout());
    }

    // ── Close ──────────────────────────────────────────────────────────────
    function finish(): void {
      if (closed) return;
      closed = true;
      resetGestures();
      for (const id of timers) deps.clearTimeout(id);
      timers.clear();
      deps.clearInterval(watch);
      resizeObserver?.disconnect();
      for (const off of cleanups.splice(0)) off();
      // The canvases' backing stores and the thumbnails go with the board.
      for (const canvas of [sheet, detail]) {
        canvas.width = 0;
        canvas.height = 0;
      }
      for (const entry of standins.values()) entry.img.removeAttribute('src');
      card.remove();
      resolve();
    }

    // ── Open ───────────────────────────────────────────────────────────────
    refresh();
    let tries = 0;
    const start = (): void => {
      if (closed) return;
      reserveBar();
      if (!layout()) {
        // Not on screen yet (the dialog is still being laid out).
        if (++tries < 30) deps.raf(start);
        return;
      }
      if (opts.focus && boxById.has(opts.focus)) {
        selection = { kind: 'slot', id: opts.focus };
        showSlot(opts.focus, 2, false);
        refresh();
        try {
          slotButtons.get(opts.focus)?.focus?.({ preventScroll: true });
        } catch {
          /* focus is a nicety */
        }
      } else {
        try {
          stage.focus?.({ preventScroll: true });
        } catch {
          /* focus is a nicety */
        }
      }
      if (sheetGen === 0) scheduleSheet(0);
    };
    refreshStandins();
    start();
  });
}

// ── Small helpers ────────────────────────────────────────────────────────────

/** The focused element inside the board's shadow root (or the document). */
function activeElementOf(node: HTMLElement): Element | null {
  const root = typeof node.getRootNode === 'function' ? (node.getRootNode() as Document | ShadowRoot) : undefined;
  const active = (root as { activeElement?: Element | null } | undefined)?.activeElement;
  if (active !== undefined) return active;
  return typeof document !== 'undefined' ? document.activeElement : null;
}

function sameTarget(a: HitTarget, b: HitTarget): boolean {
  return a.kind === b.kind && (a.kind === 'background' || (b.kind !== 'background' && a.id === b.id));
}

/** Every element under `node`, depth first (the board's own small trees). */
function descendants(node: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (const child of Array.from(node.children ?? []) as HTMLElement[]) out.push(child, ...descendants(child));
  return out;
}

function isInside(node: Element | null, container: HTMLElement): boolean {
  let at = node as (Element & { parentElement?: Element | null }) | null;
  while (at) {
    if (at === container) return true;
    at = at.parentElement ?? null;
  }
  return false;
}

/** A picture's pixel size, loaded once (only when the answer does not know it). */
function naturalSize(url: string): Promise<{ w: number; h: number } | undefined> {
  return new Promise((resolve) => {
    if (typeof Image === 'undefined') return resolve(undefined);
    const image = new Image();
    image.onload = () => resolve(image.naturalWidth > 0 ? { w: image.naturalWidth, h: image.naturalHeight } : undefined);
    image.onerror = () => resolve(undefined);
    image.src = url;
  });
}
